import type { FC } from 'react';
import React, { useContext } from 'react';

import { type ApiError } from '../../checkout-v2/types';
import noop from '../../../utils/function/noop';
import { AppContext } from '../../contextStore/AppContext';
import { NotificationContext } from '../../contextStore/NotificationContext';
import { type SetupPaymentMethodResponse } from '../../../services/customer/types';
import useSessionExpirationReminder from '../../../hooks/useSessionExpirationReminder';
import SycurioPhoneEntrySubmitView, {
  type SycurioPaymentMethodFormSubmitData,
} from '../sycurio-phone-entry-submit-view/SycurioPhoneEntrySubmitView';
import type ComponentEntryLocalData from '../../../shared/types/component-entry/ComponentEntryLocalData';
import type CustomerApi from '../../../services/customer/CustomerApi';
import ErrorCard from '../../../components/ErrorCard/ErrorCard';
import useSycurioLogger from '../hooks/useSycurioLogger';
import { generateEventMetadataFromSycurioUrl } from '../utilities/generateEventMetadataFromSycurioUrl';
import {
  generateSycurioData,
  isTelephonicEntryLocalDataValid,
} from '../utilities/generateSycurioSubmitObject';
import { isApiError } from '../../../utils/function/isApiError';
import usePollingIntervalController from '../../../hooks/usePollingIntervalController/usePollingIntervalController';
import { useSycurioPhoneEntrySubmitSessionDetails } from '../hooks/useSycurioPhoneEntrySubmitSessionDetails';
import { generateCreatePaymentMethodPayload } from '../utilities/generateCreatePaymentMethodPayload';
import { assertValidSetupPaymentMethodResponse } from '../utilities/assertValidSetupPaymentMethodResponse';
import { getPaymentMethodIdFromSetupPaymentMethodResponse } from '../utilities/getPaymentMethodIdFromSetupPaymentMethodResponse';
import { assertIsValueString } from '../../../utils/asserts/assertIsValueString';

export type SycurioPhoneEntrySubmitContainerProps = {
  localData: ComponentEntryLocalData;
  customerId: string;
  customerApi: typeof CustomerApi;
  onSuccess: ({
    paymentMethodId,
    showAddPaymentMethodNotificationAfterNav,
  }: {
    paymentMethodId: string;
    showAddPaymentMethodNotificationAfterNav?: boolean;
  }) => void;
  onBeforeSubmit?: () => void;
  onBackClick?: () => void;
  onCancel: () => void;
  onError?: (error: ApiError) => void;
};

export type SycurioCreatePaymentMethodProps = {
  nickname: string;
  nameOnCard: string;
  manufacturerCard: boolean;
  isDefault: boolean;
};

const SycurioPhoneEntrySubmitContainer: FC<
  SycurioPhoneEntrySubmitContainerProps
> = ({
  customerApi,
  customerId,
  localData,
  onBeforeSubmit = noop,
  onSuccess = noop,
  onCancel = noop,
  onBackClick,
  onError = noop,
}) => {
  const { setData, originalCheckoutSessionResponse } =
    useContext(AppContext);

  const { trackEvent, logError } = useSycurioLogger();

  const { notify } = useContext(NotificationContext);

  const { setIsComponentUnmounting, setIsSubmitDone } =
    usePollingIntervalController({ customerId });

  const {
    telephonicEntryDetails,
    agent,
    checkoutSessionId,
    merchantId,
    sycurioMetadata,
  } = useSycurioPhoneEntrySubmitSessionDetails({
    sessionResponse: originalCheckoutSessionResponse,
  });

  const handleError = (err: ApiError) => {
    setData({ overlayLoaderConfig: { show: false } });

    if (onBackClick) {
      onBackClick();
    }
    notify({
      severity: 'error',
      title: err.detail,
    });
  };

  const notifyPaymentMethodWarning = (
    setupIntentResponse?: SetupPaymentMethodResponse,
  ) => {
    if (setupIntentResponse?.data.warning) {
      setData({
        addPaymentMethodWarning:
          setupIntentResponse?.data.warning,
      });
    }
  };

  const createPaymentMethod = async ({
    nickname,
    nameOnCard,
    manufacturerCard,
    isDefault,
  }: SycurioCreatePaymentMethodProps) => {
    try {
      assertIsValueString(
        localData?.paymentMethodId,
        'Failed to add payment method',
      );

      const createPaymentMethodResponse =
        await customerApi.createPaymentMethod(
          customerId,
          generateCreatePaymentMethodPayload({
            nameOnCard,
            nickname,
            manufacturerCard,
            isDefault,
            agent,
            vendorPaymentMethodId: localData.paymentMethodId,
          }),
        );

      assertValidSetupPaymentMethodResponse(
        createPaymentMethodResponse,
      );

      const paymentMethodId =
        getPaymentMethodIdFromSetupPaymentMethodResponse(
          createPaymentMethodResponse,
        );

      trackEvent(sycurioMetadata);

      onSuccess({
        paymentMethodId,
        showAddPaymentMethodNotificationAfterNav:
          !createPaymentMethodResponse?.data.warning,
      });

      notifyPaymentMethodWarning(createPaymentMethodResponse);
    } catch (e: any) {
      onError(e?.response?.data as ApiError);

      if (
        e instanceof Error &&
        telephonicEntryDetails &&
        'url' in telephonicEntryDetails
      ) {
        logError({
          error: e,
          metadata: generateEventMetadataFromSycurioUrl({
            checkoutSessionId,
            merchantId,
            sycurioFragmentUrl: telephonicEntryDetails?.url,
          }),
        });
      }

      const data = e?.response?.data;
      if (isApiError(data)) {
        handleError(data);
      } else {
        handleError({
          title: 'ERROR',
          status: 400,
          detail:
            data?.message ?? 'Failed to add payment method',
        });
      }
    } finally {
      setData({ overlayLoaderConfig: { show: false } });
    }
  };

  const onSubmit = ({
    nameOnCard,
    nickname,
    manufacturerCard,
    isDefault,
  }: SycurioPaymentMethodFormSubmitData) => {
    setData({ overlayLoaderConfig: { show: true } });
    onBeforeSubmit();

    createPaymentMethod({
      nameOnCard,
      nickname: nickname || '',
      manufacturerCard,
      isDefault,
    })
      .catch(() => {
        // errors are already caught and handled inside createPaymentMethod itself
      })
      .finally(() => {
        setIsComponentUnmounting(false);
        setIsSubmitDone(true);
      });
  };

  useSessionExpirationReminder({
    notify,
    originalCheckoutSessionResponse,
  });

  if (!isTelephonicEntryLocalDataValid(localData)) {
    return <ErrorCard />;
  }

  return (
    <SycurioPhoneEntrySubmitView
      formTitle="Add via phone entry"
      sycurioData={generateSycurioData(localData)}
      agent={agent}
      onBackClick={onBackClick}
      onCancel={onCancel}
      onSubmit={onSubmit}
    />
  );
};

export default SycurioPhoneEntrySubmitContainer;
